import { apiClient } from "~/services/api-client";
import { ApiError } from "~/shared/utils/api-error";
import { isAxiosError } from "axios";
import type { CallFlowType } from "~/features/orders/types-interfaces";
import type {
  TransferQueueRecord,
  TransferQueuePagination,
  TransferQueueApiResponse,
  CreateTransferQueueRequest,
  CreateTransferQueueResponse,
  UpdateTransferQueueRequest,
  UpdateTransferQueueResponse,
  LockTransferQueueResponse,
  UnlockTransferQueueResponse,
} from "./types";

export type { TransferQueueRecord, TransferQueuePagination } from "./types";

/**
 * Fetch a paginated page of transfer queue records.
 * Returns both the records for the requested page and the server pagination metadata.
 */
function buildTransferQueueParams(
  page: number,
  limit: number,
  search?: string,
  filters?: { cities?: string[]; locationCodes?: string[] }
): Record<string, string | number> {
  const params: Record<string, string | number> = { page, limit };
  if (search) params.search = search;
  // Serialize arrays as comma-separated strings — the backend expects this format
  if (filters?.cities?.length) params.cities = filters.cities.join(',');
  if (filters?.locationCodes?.length) params.locationCodes = filters.locationCodes.join(',');
  return params;
}

export async function fetchTransferQueue(
  page = 1,
  limit = 10,
  search?: string,
  filters?: { cities?: string[]; locationCodes?: string[] }
): Promise<{ records: TransferQueueRecord[]; pagination: TransferQueuePagination; contextualCities: string[] }> {
  try {
    const response = await apiClient.get<TransferQueueApiResponse>('/transfer-queue', {
      params: buildTransferQueueParams(page, limit, search, filters),
    });

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'API request was not successful',
        response.status,
        response.data
      );
    }

    const defaultPagination: TransferQueuePagination = {
      total: 0,
      page,
      limit,
      totalPages: 0,
    };

    return {
      records: response.data.data ?? [],
      pagination: response.data.pagination ?? defaultPagination,
      contextualCities: response.data.filtersContextual?.cities ?? [],
    };
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to fetch transfer queue from API';

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Create a transfer queue record
 */
export async function createTransferQueue(data: CreateTransferQueueRequest): Promise<TransferQueueRecord> {
  try {
    const response = await apiClient.post<CreateTransferQueueResponse>('/transfer-queue', data);

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to create transfer queue record',
        response.status,
        response.data
      );
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to create transfer queue record';

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Update a transfer queue record by orderId
 */
export async function updateTransferQueue(
  orderId: string,
  data: UpdateTransferQueueRequest
): Promise<TransferQueueRecord> {
  try {
    const response = await apiClient.patch<UpdateTransferQueueResponse>(
      `/transfer-queue/${orderId}`,
      data
    );

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to update transfer queue record',
        response.status,
        response.data
      );
    }

    return response.data.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to update transfer queue record';

      throw new ApiError(message, statusCode, error.response?.data);
    }

    if (error instanceof ApiError) {
      throw error;
    }

    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Reset a transfer queue order back to autocall.
 * PATCH /transfer-queue/:orderId with { resetToAutocall: true }
 */
export async function resetTransferQueueToAutocall(orderId: string): Promise<void> {
  try {
    const response = await apiClient.patch<{ success: boolean; message?: string }>(
      `/transfer-queue/${encodeURIComponent(orderId)}`,
      { resetToAutocall: true }
    );

    if (!response.data?.success) {
      throw new ApiError(
        response.data?.message ?? 'Failed to reset order to autocall',
        response.status,
        response.data
      );
    }
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to reset order to autocall';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Lock a transfer queue order (before editing).
 * POST /transfer-queue/:orderId/lock
 */
export async function lockTransferQueueOrder(orderId: string): Promise<LockTransferQueueResponse> {
  try {
    const response = await apiClient.post<LockTransferQueueResponse>(
      `/transfer-queue/${encodeURIComponent(orderId)}/lock`
    );

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to lock order',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string; data?: unknown } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to lock order';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

/**
 * Unlock a transfer queue order (after cancel / save).
 * POST /autocall/orders/:orderId/unlock
 */
export async function unlockTransferQueueOrder(orderId: string): Promise<UnlockTransferQueueResponse> {
  try {
    const response = await apiClient.post<UnlockTransferQueueResponse>(
      `/transfer-queue/${encodeURIComponent(orderId)}/unlock`
    );

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to unlock order',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to unlock order';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}

export type StartManualTransferQueueCallRequest = {
  phoneNumber: string;
  callFlowType?: CallFlowType;
};

export type StartManualTransferQueueCallResponse = {
  success: boolean;
  message?: string;
  data?: {
    orderId: string;
    callFlowType?: string;
    contactId?: string;
    phoneNumber?: string;
    createdBy?: string;
    createdAt?: string;
  };
};

/**
 * Start manual call for a transfer queue order.
 * POST /transfer-queue/:orderId/start-call
 */
export async function startManualTransferQueueCall(
  orderId: string,
  data: StartManualTransferQueueCallRequest
): Promise<StartManualTransferQueueCallResponse> {
  try {
    const response = await apiClient.post<StartManualTransferQueueCallResponse>(
      `/transfer-queue/${encodeURIComponent(orderId)}/start-call`,
      data
    );

    if (!response.data) {
      throw new ApiError('Empty response from API', response.status);
    }

    if (!response.data.success) {
      throw new ApiError(
        response.data.message ?? 'Failed to initiate call',
        response.status,
        response.data
      );
    }

    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.status;
      const responseData = error.response?.data as { message?: string } | undefined;
      const message = responseData?.message ?? error.message ?? 'Failed to initiate call';
      throw new ApiError(message, statusCode, error.response?.data);
    }
    if (error instanceof ApiError) throw error;
    throw new ApiError(
      error instanceof Error ? error.message : 'An unexpected error occurred',
      undefined,
      error
    );
  }
}
